// CRUD admin de horarios.

import { useEffect, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Plus, Edit2, Trash2, AlertCircle, Loader2, X } from 'lucide-react';
import { createPortal } from 'react-dom';

import {
  listHorarios, createHorario, updateHorario, deleteHorario,
  listClasesTipos, listProfesores,
} from '../../lib/adminApi';
import { ApiError } from '../../lib/api';

const FORMATOS = ['presencial', 'online', 'hibrido'];
const DIAS = [
  { v: 1, l: 'Lunes' }, { v: 2, l: 'Martes' }, { v: 3, l: 'Miércoles' },
  { v: 4, l: 'Jueves' }, { v: 5, l: 'Viernes' }, { v: 6, l: 'Sábado' }, { v: 7, l: 'Domingo' },
];

const FORM_VACIO = {
  clase_tipo_id: '', profesor_id: '',
  dia_semana: 1, hora_inicio: '09:00', duracion_minutos: 60,
  formato: 'presencial', aforo_maximo: 12, sala: '',
  cancelacion_horas_antes: 6, activo: true,
  fecha_inicio_vigencia: '', fecha_fin_vigencia: '',
};

export default function AdminHorarios() {
  const { t } = useTranslation();
  const [items, setItems] = useState([]);
  const [clasesTipos, setClasesTipos] = useState([]);
  const [profesores, setProfesores] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [editar, setEditar] = useState(null);

  const cargar = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const [hr, ct, pr] = await Promise.all([listHorarios(), listClasesTipos(), listProfesores()]);
      setItems(hr.data || []);
      setClasesTipos(ct.data || []);
      setProfesores(pr.data || []);
    } catch (e) { setError(e); } finally { setLoading(false); }
  }, []);
  useEffect(() => { cargar(); }, [cargar]);

  const onBorrar = async (h) => {
    const claseNom = h.clase_tipo?.nombre_es || '?';
    if (!window.confirm(t('admin.confirmar_desactivar', { nombre: `${claseNom} ${DIAS[h.dia_semana - 1]?.l || ''} ${h.hora_inicio?.slice(0, 5)}` }))) return;
    try { await deleteHorario(h.id); cargar(); }
    catch (e) { alert(e.message || t('admin.error_generico')); }
  };

  return (
    <div className="admin-page">
      <header className="admin-page-head">
        <div>
          <h1>{t('admin.horarios.titulo')}</h1>
          <p className="admin-page-sub">{t('admin.horarios.sub')}</p>
        </div>
        <button type="button" className="btn-primary" onClick={() => setEditar(FORM_VACIO)}>
          <Plus size={16} strokeWidth={1.75} /> {t('admin.crear_nuevo')}
        </button>
      </header>

      {loading && <div className="admin-loading"><Loader2 size={24} className="spin" /></div>}
      {error && <div className="admin-error"><AlertCircle size={20} /><span>{error.message}</span></div>}

      {!loading && !error && (
        <table className="admin-table">
          <thead>
            <tr>
              <th>{t('admin.horarios.col.dia')}</th>
              <th>{t('admin.horarios.col.hora')}</th>
              <th>{t('admin.horarios.col.clase')}</th>
              <th>{t('admin.horarios.col.profesor')}</th>
              <th>{t('admin.horarios.col.formato')}</th>
              <th>{t('admin.horarios.col.aforo')}</th>
              <th>{t('admin.horarios.col.activo')}</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {items.map((h) => (
              <tr key={h.id} className={!h.activo ? 'inactivo' : ''}>
                <td>{DIAS[h.dia_semana - 1]?.l}</td>
                <td>{h.hora_inicio?.slice(0, 5)} ({h.duracion_minutos} min)</td>
                <td>{h.clase_tipo?.nombre_es || '—'}</td>
                <td>{h.profesor?.nombre_publico || '—'}</td>
                <td>{h.formato}</td>
                <td>{h.aforo_maximo}</td>
                <td>{h.activo ? '✓' : '—'}</td>
                <td className="admin-acciones">
                  <button type="button" onClick={() => setEditar({
                    ...FORM_VACIO,
                    ...h,
                    clase_tipo_id: h.clase_tipo?.id || '',
                    profesor_id: h.profesor?.id || '',
                    fecha_inicio_vigencia: h.fecha_inicio_vigencia || '',
                    fecha_fin_vigencia: h.fecha_fin_vigencia || '',
                    hora_inicio: h.hora_inicio?.slice(0, 5) || '',
                  })} aria-label={t('admin.editar')}>
                    <Edit2 size={16} />
                  </button>
                  <button type="button" onClick={() => onBorrar(h)} aria-label={t('admin.borrar')}>
                    <Trash2 size={16} />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {editar && (
        <HorarioModal
          item={editar} clasesTipos={clasesTipos} profesores={profesores}
          onCerrar={() => setEditar(null)} onGuardado={() => { setEditar(null); cargar(); }}
        />
      )}
    </div>
  );
}

function HorarioModal({ item, clasesTipos, profesores, onCerrar, onGuardado }) {
  const { t } = useTranslation();
  const [form, setForm] = useState(item);
  const [enviando, setEnviando] = useState(false);
  const [error, setError] = useState(null);
  const editando = !!item.id;

  const setCampo = (k) => (e) => {
    const v = e.target.type === 'checkbox' ? e.target.checked : e.target.value;
    setForm((f) => ({ ...f, [k]: v }));
  };

  const submit = async (e) => {
    e.preventDefault();
    setError(null); setEnviando(true);
    try {
      const payload = {
        clase_tipo_id: form.clase_tipo_id,
        profesor_id: form.profesor_id,
        dia_semana: Number(form.dia_semana),
        hora_inicio: form.hora_inicio,
        duracion_minutos: Number(form.duracion_minutos),
        formato: form.formato,
        aforo_maximo: Number(form.aforo_maximo),
        sala: form.sala || null,
        cancelacion_horas_antes: Number(form.cancelacion_horas_antes),
        activo: !!form.activo,
        fecha_inicio_vigencia: form.fecha_inicio_vigencia || null,
        fecha_fin_vigencia: form.fecha_fin_vigencia || null,
      };
      if (editando) await updateHorario(item.id, payload);
      else await createHorario(payload);
      onGuardado();
    } catch (e) {
      setError(e instanceof ApiError ? e.message : t('admin.error_generico'));
    } finally { setEnviando(false); }
  };

  return createPortal(
    <div className="modal-backdrop" onClick={() => !enviando && onCerrar()}>
      <div className="modal modal-admin" onClick={(e) => e.stopPropagation()}>
        <button type="button" className="modal-cerrar" onClick={onCerrar}><X size={20} /></button>
        <h2 className="modal-titulo">{editando ? t('admin.editar') : t('admin.crear_nuevo')}</h2>

        <form className="modal-form" onSubmit={submit}>
          <div className="admin-form-grid">
            <div className="modal-campo">
              <label>{t('admin.horarios.f.clase')}</label>
              <select value={form.clase_tipo_id} onChange={setCampo('clase_tipo_id')} required>
                <option value="">—</option>
                {clasesTipos.map((c) => <option key={c.id} value={c.id}>{c.nombre_es}</option>)}
              </select>
            </div>
            <div className="modal-campo">
              <label>{t('admin.horarios.f.profesor')}</label>
              <select value={form.profesor_id} onChange={setCampo('profesor_id')} required>
                <option value="">—</option>
                {profesores.map((p) => <option key={p.id} value={p.id}>{p.nombre_publico}</option>)}
              </select>
            </div>
            <div className="modal-campo">
              <label>{t('admin.horarios.f.dia')}</label>
              <select value={form.dia_semana} onChange={setCampo('dia_semana')}>
                {DIAS.map((d) => <option key={d.v} value={d.v}>{d.l}</option>)}
              </select>
            </div>
            <div className="modal-campo">
              <label>{t('admin.horarios.f.hora')}</label>
              <input type="time" value={form.hora_inicio} onChange={setCampo('hora_inicio')} required />
            </div>
            <div className="modal-campo">
              <label>{t('admin.horarios.f.duracion')}</label>
              <input type="number" min="1" value={form.duracion_minutos} onChange={setCampo('duracion_minutos')} required />
            </div>
            <div className="modal-campo">
              <label>{t('admin.horarios.f.formato')}</label>
              <select value={form.formato} onChange={setCampo('formato')}>
                {FORMATOS.map((f) => <option key={f} value={f}>{f}</option>)}
              </select>
            </div>
            <div className="modal-campo">
              <label>{t('admin.horarios.f.aforo')}</label>
              <input type="number" min="1" value={form.aforo_maximo} onChange={setCampo('aforo_maximo')} required />
            </div>
            <div className="modal-campo">
              <label>{t('admin.horarios.f.sala')}</label>
              <input type="text" value={form.sala || ''} onChange={setCampo('sala')} />
            </div>
            <div className="modal-campo">
              <label>{t('admin.horarios.f.cancelacion')}</label>
              <input type="number" min="0" value={form.cancelacion_horas_antes} onChange={setCampo('cancelacion_horas_antes')} />
            </div>
            <div className="modal-campo">
              <label>{t('admin.horarios.f.fecha_inicio')}</label>
              <input type="date" value={form.fecha_inicio_vigencia || ''} onChange={setCampo('fecha_inicio_vigencia')} />
            </div>
            <div className="modal-campo">
              <label>{t('admin.horarios.f.fecha_fin')}</label>
              <input type="date" value={form.fecha_fin_vigencia || ''} onChange={setCampo('fecha_fin_vigencia')} />
            </div>
            <div className="modal-campo full">
              <label>
                <input type="checkbox" checked={!!form.activo} onChange={setCampo('activo')} />
                {' '}{t('admin.horarios.f.activo')}
              </label>
            </div>
          </div>

          {error && <div className="modal-error-general"><AlertCircle size={16} /><span>{error}</span></div>}

          <div className="modal-acciones">
            <button type="button" className="btn-secondary" onClick={onCerrar} disabled={enviando}>{t('comun.cancelar')}</button>
            <button type="submit" className="btn-primary" disabled={enviando}>
              {enviando ? <Loader2 size={16} className="spin" /> : t('comun.guardar')}
            </button>
          </div>
        </form>
      </div>
    </div>,
    document.body
  );
}
